/**
 * LicenseProvider — session-scoped license state.
 *
 * Tek /license çağrısını paylaşarak hem License sayfası, hem Topbar
 * (aylık kullanım badge), hem Sidebar (footer plan rozeti) aynı state'i
 * kullanır. Otomatik refresh: session değişince (login/logout) yeniden çeker.
 *
 *   state: null    -> loading
 *   state: false   -> not fetched / no session
 *   state: obj     -> license
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const LicenseContext = createContext(null);

export function LicenseProvider({ children }) {
    const { session } = useAuth();
    const [license, setLicense] = useState(null); // null | false | object
    // Local override — DEV plan switcher preview'ı için. Backend'e dokunmaz.
    const [override, setOverride] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/license");
            setLicense(data);
            return data;
        } catch {
            setLicense(false);
            return false;
        }
    }, []);

    useEffect(() => {
        if (session && session.user) {
            refresh();
        } else if (session === false) {
            setLicense(false);
            setOverride(null);
        }
    }, [session, refresh]);

    // Effective license = override (dev) if set, else server value.
    const effective = override || license;

    return (
        <LicenseContext.Provider value={{ license: effective, refresh, override, setOverride }}>
            {children}
        </LicenseContext.Provider>
    );
}

export const useLicense = () => {
    const ctx = useContext(LicenseContext);
    if (!ctx) throw new Error("useLicense must be used within LicenseProvider");
    return ctx;
};

/* ============================================================================
   Yardımcılar — kullanım verisi ve kota hesabı
   ----------------------------------------------------------------------------
   Backend `usage_this_month` field'ı artık gerçek widget sayacından okunur
   (widget_query_log + widget_events toplamı). Yoksa 0 döner.
============================================================================ */
export function getUsageThisMonth(license) {
    if (!license) return null;
    if (typeof license.usage_this_month === "number") return license.usage_this_month;
    return 0;
}

export function getUsagePct(license) {
    const usage = getUsageThisMonth(license);
    const quota = license?.quota_this_month;
    if (usage === null || !quota) return null;
    return Math.min(100, (usage / quota) * 100);
}
